import {getTestProps} from '@benchling/js-test-utils';
import Backbone from 'backbone';
import classNames from 'classnames';
import $ from 'jquery';
import React, {useCallback, useLayoutEffect, useRef, useState} from 'react';

import {resizeThrottler} from 'BenchlingUtil';
import {
  BufferViewType,
  OptionsViewMetadata,
  TabViewMetadata,
  TabViewWithOptionalAfterAttachCallback,
} from 'buffers/BufferTypes';
import {TabState} from 'Navigation';
import useResizeObserver from 'ui-library/hooks/useResizeObserver';
import {GenericBackboneViewClass, Optional} from 'util/Types';

interface BufferViewLayoutAttributes {
  disableResize: boolean;
  el: null | JQuery;
  width: number;
  height: number;
  menu: boolean; // Dead code?
}

interface BufferViewLayout {
  get<K extends keyof BufferViewLayoutAttributes>(key: K): BufferViewLayoutAttributes[K];

  set<K extends keyof BufferViewLayoutAttributes>(
    attributeName: K,
    value: BufferViewLayoutAttributes[K],
    options?: Backbone.ModelSetOptions
  ): BufferViewLayout;

  set(obj: Partial<BufferViewLayoutAttributes>): BufferViewLayout;
}

/*
 * BufferViewLayout: model class that allows for imperatively resizing tabs. Currently used
 * only by sequence maps. New code should rely on `useResizeObserver` instead.
 */
class BufferViewLayout extends Backbone.Model {
  override defaults(): BufferViewLayoutAttributes {
    return {
      disableResize: false,
      el: null,
      width: 0,
      height: 0,
      menu: false,
    };
  }

  resize(): void {
    // In some cases we want to disable updates based on resizing, like when rendering a tab for
    // a PDF where we want to hard-code the dimensions.
    const el = this.get('el');
    if (this.get('disableResize') || el == null) {
      return;
    }
    this.set({
      width: el.find('.buffer-tab-wrapper').width(),
      height: el.height(),
    });
  }
}

// NOTE: this class was intended to be replaced by the BufferContent component
// in https://github.com/benchling/aurelia/pull/45798/ but caused rendering issues
// with Tooltips not being removed when changing buffers. Keeping them both for now
// since the BufferContent component can be used for rendering tabs in Storybook.
interface BufferContentViewTabView extends Backbone.View<TabState> {
  afterAttach?(): void;
}

class BufferContentView extends Backbone.View<BufferViewLayout> {
  tabView: Optional<BufferContentViewTabView>;

  static initClass(): void {
    this.prototype.className = 'hide';
  }

  override initialize(options: Backbone.ViewOptions<BufferViewLayout>): void {
    this.model = new BufferViewLayout({
      el: this.$el,
      width: this.$el.find('.buffer-tab-wrapper').width(),
      height: this.$el.height(),
    });
    $(window).on(
      'resize',
      resizeThrottler(() => {
        this.model.resize();
      })
    );
    $(window).on('buffersResize', () => {
      this.model.resize();
    });
  }

  update(
    tabState: TabState | null,
    tabViewCls: GenericBackboneViewClass<TabState, BufferContentViewTabView> | null
  ): void {
    if (this.tabView != null) {
      this.tabView.remove();
      this.tabView = null;
    }

    if (tabState != null) {
      tabState.set({bufferViewLayout: this.model});

      if (tabViewCls == null) {
        throw new Error('Instantiated TabState object has no associated TabView to show');
      }
      // eslint-disable-next-line new-cap
      this.tabView = new tabViewCls({model: tabState});
      this.$el.toggleClass('buffer-options-align', tabState.get('options'));
      this.$el.toggleClass('buffer-content--grey', tabState.get('greyTab'));
      this.$el.find('.buffer-tab-wrapper').html(this.tabView.render().el);
      if (typeof this.tabView.afterAttach === 'function') {
        this.tabView.afterAttach();
      }

      // Recalculate layout height after render.
      // eslint-disable-next-line @typescript-eslint/no-confusing-void-expression
      return this.model.resize();
    }
  }
}

BufferContentView.initClass();

/* When a Backbone View is removed, it calls `_removeElement` which removes its element tree from the DOM.
 * Therefore, it's better to render the View outside of the Document entirely, and then set the contents
 * using JQuery html: https://api.jquery.com/html/#html2
 * rather than instantiating the View class with {el: div} since that will lead to React warnings when it
 * gets removed.
 */

function TabOptions({
  tabState,
  optionsViewMetadata,
}: {
  tabState: Optional<TabState>;
  optionsViewMetadata: Optional<OptionsViewMetadata>;
}): JSX.Element {
  const [div, setDiv] = useState<null | HTMLDivElement>(null);
  const tabOptionsViewRef = useRef<Optional<Backbone.View<TabState>>>(null);

  useLayoutEffect(() => {
    if (optionsViewMetadata?.optionsViewType === BufferViewType.BACKBONE) {
      const {OptionsView} = optionsViewMetadata;
      if (tabState != null && div != null) {
        const tabOptionsView = new OptionsView({model: tabState});
        $(div).html(tabOptionsView.render().el);
        tabOptionsViewRef.current = tabOptionsView;
      }
    }
    return () => {
      tabOptionsViewRef.current?.remove();
      tabOptionsViewRef.current = null;
    };
  });

  // Always render div regardless of props, although it may be styled as `{display: none}`
  // when a parent element has the `hide-options-view` class.
  return (
    <div
      ref={setDiv}
      className={classNames('buffer-options', 'buffer-options--builtin')}
      {...getTestProps({
        component: 'TabOptions',
        element: 'root',
      })}
    >
      {tabState != null && optionsViewMetadata?.optionsViewType === BufferViewType.REACT && (
        <div className={optionsViewMetadata.additionalOptionsClassNames ?? ''}>
          <optionsViewMetadata.OptionsView {...optionsViewMetadata.propsFromTabState(tabState)} />
        </div>
      )}
    </div>
  );
}

export function useBufferViewLayout(tabContentDiv: HTMLDivElement | null): BufferViewLayout {
  const bufferViewLayoutRef = useRef(new BufferViewLayout());
  const {height, width} = useResizeObserver<HTMLDivElement>({ref: tabContentDiv});
  const throttledResizeCallback = useCallback(() => {
    resizeThrottler(() => {
      bufferViewLayoutRef.current.resize();
    })();
  }, [bufferViewLayoutRef]);

  useLayoutEffect(() => {
    if (tabContentDiv) {
      bufferViewLayoutRef.current.set({
        el: $(tabContentDiv),
      });
      bufferViewLayoutRef.current.resize();
    } else {
      bufferViewLayoutRef.current.set({el: null});
    }
  }, [tabContentDiv]);

  useLayoutEffect(() => {
    throttledResizeCallback();
  }, [throttledResizeCallback, height, width]);

  return bufferViewLayoutRef.current;
}

function TabContent({
  tabState,
  tabViewMetadata,
}: {
  tabState: Optional<TabState>;
  tabViewMetadata: Optional<TabViewMetadata>;
}): JSX.Element {
  const [div, setDiv] = useState<null | HTMLDivElement>(null);
  const [tabContentDiv, setTabContentDiv] = useState<null | HTMLDivElement>(null);
  const tabContentViewRef = useRef<Optional<TabViewWithOptionalAfterAttachCallback>>(null);
  const bufferViewLayout = useBufferViewLayout(tabContentDiv);

  if (tabState != null && tabViewMetadata == null) {
    throw new Error('Instantiated TabState object has no associated TabView to show');
  }

  useLayoutEffect(() => {
    if (tabState != null) {
      tabState.set({bufferViewLayout});
      if (tabViewMetadata?.tabViewType === BufferViewType.BACKBONE) {
        const {TabView} = tabViewMetadata;
        if (TabView != null && div != null) {
          const tabContentView = new TabView({model: tabState});
          $(div).html(tabContentView.render().el);
          if (typeof tabContentView.afterAttach === 'function') {
            tabContentView.afterAttach();
          }
          tabContentViewRef.current = tabContentView;
        }
      }
      // Recalculate layout height after render.
      bufferViewLayout.resize();
    }
    return () => {
      tabContentViewRef.current?.remove();
      tabContentViewRef.current = null;
    };
    // bufferViewLayout is stable, as it is the result of a useRef, but the linter doesn't know about anything
    // that's not directly from React.
  }, [tabState, tabViewMetadata, div, bufferViewLayout]);

  return (
    <div
      ref={setTabContentDiv}
      className={classNames('buffer-content', {
        'buffer-options-align': tabState?.get('options'),
        'buffer-content-grey': tabState?.get('greyTab'),
      })}
      {...getTestProps({
        component: 'TabContent',
        element: 'root',
      })}
    >
      <div className="buffer">
        <div
          ref={setDiv}
          className="buffer-tab-wrapper"
          {...getTestProps({
            component: 'TabContent',
            element: 'bufferTabWrapper',
          })}
        >
          {tabViewMetadata?.tabViewType === BufferViewType.REACT && tabState != null && (
            <div
              className={classNames('buffer-tab', tabViewMetadata.additionalTabClassNames ?? '')}
              {...getTestProps({
                component: 'TabContent',
                element: 'bufferTab',
              })}
            >
              <tabViewMetadata.TabView {...tabViewMetadata?.propsFromTabState(tabState)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BufferContentComponent({
  tabState,
  tabContentMetadata,
  tabOptionsMetadata,
}: {
  tabState: Optional<TabState>;
  tabContentMetadata: TabViewMetadata;
  tabOptionsMetadata?: OptionsViewMetadata;
}): JSX.Element {
  // cleanup: could apply hide-options-view class at this level instead of higher in the tree.
  return (
    <div className="buffer-contentWrapper-reset">
      <TabOptions tabState={tabState} optionsViewMetadata={tabOptionsMetadata} />
      <TabContent tabState={tabState} tabViewMetadata={tabContentMetadata} />
    </div>
  );
}

const BufferContent = React.memo(BufferContentComponent);

export {BufferContent, BufferContentView, TabOptions, TabContent, BufferViewLayout};
